/**
 * @module engine/persistence
 * @summary Debounced localStorage autosave + session restore.
 * @description Autosaves the document 1.5 s after the last change; offers to
 *   restore it on next load. Uses the EXACT original key (jcc_pfd_autosave_v7)
 *   so prior sessions are recoverable.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3985-4020.
 */
import { S, emit, markDirty } from './state';
import { KEYS, autosaveKey, lsSet, lsGet, lsRemove } from '../lib/storage';
import { getCurrentUser } from '../lib/auth';
import { toast } from '../lib/toast';
import { syncShapeCounter } from './factory';
import type { DocSettings, PfdDocument, RouteMode } from '../types';

/** Drawing-wide defaults. A setting still at its default is not written out. */
const DEFAULTS = { routeMode: 'manhattan' as RouteMode, scaleMm: 25, heightScale: 1 };

/**
 * The document's settings, or undefined when every one is still default.
 *
 * Omitting the block when nothing has been changed is what lets a drawing made
 * before these existed round-trip byte for byte.
 */
export function docSettings(): DocSettings | undefined {
  const out: DocSettings = {};
  if (S.routeMode !== DEFAULTS.routeMode) out.routeMode = S.routeMode;
  if (S.scaleMm !== DEFAULTS.scaleMm) out.scaleMm = S.scaleMm;
  if (S.heightScale !== DEFAULTS.heightScale) out.heightScale = S.heightScale;
  return Object.keys(out).length ? out : undefined;
}

/**
 * Apply a loaded document's settings, falling back to the default for anything
 * it does not carry — so loading an older drawing resets cleanly rather than
 * inheriting whatever the last document left behind.
 */
export function applyDocSettings(st: DocSettings | undefined): void {
  S.routeMode = st?.routeMode ?? DEFAULTS.routeMode;
  S.scaleMm = st?.scaleMm ?? DEFAULTS.scaleMm;
  S.heightScale = st?.heightScale ?? DEFAULTS.heightScale;
}

let autoTimer: ReturnType<typeof setTimeout> | null = null;

/** Autosave key for whoever is signed in right now. */
function myAutosaveKey(): string {
  return autosaveKey(getCurrentUser()?.username);
}

// Edits since the last manual JSON save/load. Autosave (localStorage) does NOT
// clear it — only a durable save to a file does. Drives the exit guard.
let unsaved = false;

/** True when there are edits not yet saved to a JSON file. */
export function hasUnsavedChanges(): boolean {
  return unsaved;
}

/** Mark the document as durably saved (manual JSON save or fresh file load). */
export function markSaved(): void {
  unsaved = false;
}

/**
 * Serialize the current document to a plain object.
 *
 * The ONE place that decides what a saved document contains. The JSON export
 * and the autosave both go through it, because they used to build the object
 * separately and that is precisely how a field ends up saved by one path and
 * silently dropped by the other.
 * // INVARIANT: anything added to `PfdDocument` is written here and read in
 * `applyDocument`, and `selftest/persist.ts` fails if the pair disagree.
 */
export function serialize(): PfdDocument {
  const doc: PfdDocument = { meta: S.docMeta, shapes: S.shapes, connections: S.connections };
  // Only written once the 3D view has actually been used, so files from before
  // it existed round-trip byte-for-byte.
  if (S.levels.length) doc.levels = S.levels;
  if (S.pipes3.length) doc.pipes3 = S.pipes3;
  if (S.dims3.length) doc.dims3 = S.dims3;
  if (S.formulations.length) doc.formulations = S.formulations;
  if (S.screwConfigs.length) doc.screwConfigs = S.screwConfigs;
  const st = docSettings();
  if (st) doc.settings = st;
  return doc;
}

/**
 * Load a document into the engine. The counterpart to `serialize`, and the only
 * place that reads a saved document's fields.
 *
 * `drawnBy` / `approvedBy` are deliberately not restored: they identify who is
 * working now, not who drew the file being opened.
 */
export function applyDocument(d: Partial<PfdDocument>): void {
  S.shapes = d.shapes || [];
  S.connections = d.connections || [];
  S.levels = d.levels || [];
  S.pipes3 = d.pipes3 || [];
  S.dims3 = d.dims3 || [];
  S.formulations = d.formulations || [];
  S.screwConfigs = d.screwConfigs || [];
  applyDocSettings(d.settings);
  if (d.meta) {
    const { drawnBy: _d, approvedBy: _a, ...rest } = d.meta;
    Object.assign(S.docMeta, rest);
  }
  syncShapeCounter();
}

/** Debounce an autosave (called by pushUndo and other mutators). */
export function scheduleAutoSave(): void {
  unsaved = true;
  if (autoTimer) clearTimeout(autoTimer);
  autoTimer = setTimeout(doAutoSave, 1500);
}

/** Write the current document to localStorage immediately. */
export function doAutoSave(): void {
  try {
    const data: PfdDocument = { ...serialize(), saved: Date.now() };
    lsSet(myAutosaveKey(), data);
    emit('saved'); // drives the toolbar's "Saved HH:MM" indicator
  } catch (e) {
    console.warn('Auto-save failed:', e);
  }
}

/**
 * Write a pending autosave now instead of on the debounce. Call this before
 * dropping the session: the key is resolved from the signed-in user, so a timer
 * left to fire after sign-out would write to the shared slot and hand the work
 * to whoever logs in next.
 */
export function flushAutoSave(): void {
  if (!autoTimer) return;
  clearTimeout(autoTimer);
  autoTimer = null;
  doAutoSave();
}

/** Discard the signed-in user's autosave (e.g. after an explicit manual save). */
export function clearAutoSave(): void {
  lsRemove(myAutosaveKey());
}

/**
 * If a previous autosave exists, ask the user whether to restore it.
 * @returns true if a session was restored.
 */
export function tryAutoRestore(): boolean {
  const key = myAutosaveKey();
  let d = lsGet<PfdDocument | null>(key, null);
  // Nothing under this account yet — adopt any document left by a session from
  // before autosaves were namespaced, then retire the shared key so the next
  // person to sign in on this PC doesn't inherit it too.
  if (!d && key !== KEYS.autosave) {
    d = lsGet<PfdDocument | null>(KEYS.autosave, null);
    if (d) lsRemove(KEYS.autosave);
  }
  if (!d || !d.shapes || !d.shapes.length) return false;
  const age = Math.round((Date.now() - (d.saved || 0)) / 60000);
  const ts = new Date(d.saved || Date.now()).toLocaleString();
  if (
    confirm(
      `A previous session was auto-saved.\n${d.shapes.length} shapes, ${age} min ago (${ts}).\n\nRestore it?`,
    )
  ) {
    applyDocument(d);
    // Restored work exists only in localStorage, not in a JSON file yet.
    unsaved = true;
    markDirty();
    emit('doc');
    emit('selection');
    toast('Session restored');
    return true;
  }
  // Declined — clear so it never prompts again.
  clearAutoSave();
  return false;
}
